import { Fragment, useCallback, useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { onSnapshot, or, orderBy, query, serverTimestamp, setDoc, where } from "firebase/firestore";
import { ref, uploadBytesResumable } from "firebase/storage";
import { getFunctions, httpsCallable } from "firebase/functions";
import { app, storage } from "../firebase";
import { useAuth } from "../auth";
import { dishTitle, useI18n } from "../lib/i18n";
import type { DishDoc, TastingSummary } from "../lib/types";
import { ROLE_LABEL } from "../lib/types";
import {
  isValidAxisScore,
  sortTastingEntries,
  tastingEnabled,
  toTastingView,
  type TastingAxes,
  type TastingEntry,
} from "../lib/tasting";
import { VIDEO_ACCEPT, VIDEO_TYPE_ERROR, videoContentType } from "../lib/media";
import { inspectVideoFiles, sortVideoFiles } from "../lib/parts";
import { fetchFromProxy } from "../lib/session";
import { DeleteEvaluation, RestoreEvaluation } from "../components/DeleteEvaluation";
import {
  BAND_LABEL,
  CATEGORY_LABEL,
  VERDICT_LABEL,
  groupByCategory,
  mmss,
  scoreBand,
  toView,
  type EvaluationResult,
  type EvaluationView,
} from "../lib/evaluation";

/**
 * 1日の評価回数上限の既定値(#55 設計 §4.2)。
 * 実際の上限は tenants/{tenantId}.dailyEvalLimit が正(#129)。0以下 = 無制限。
 * ここは表示専用で、強制は Functions が行う。
 */
const DEFAULT_DAILY_LIMIT = 3;

const functions = getFunctions(app, "asia-northeast1");
const requestEvaluation = httpsCallable<
  { dishId: string; videoPaths: string[] },
  { evaluationId: string; remaining: number | null }
>(functions, "requestEvaluation");

interface EvaluationDoc {
  dishId: string;
  dishName: string | null;
  traineeUid: string;
  supervisorUid: string | null;
  score: number | null;
  status: "queued" | "running" | "succeeded" | "failed";
  error: string | null;
  goldenWasApproved: boolean;
  createdAt: { toDate(): Date } | null;
  /** 食味評価の代表値(Functions トリガの非正規化)。未評価は null / 無し */
  tasting?: TastingSummary | null;
  /** 論理削除(functions/delete-evaluation.mjs)。入っていれば一覧・熟達度に出さない */
  deletedAt?: { toDate(): Date } | null;
  deletedByName?: string | null;
  deleteReason?: string | null;
}

type Row = EvaluationDoc & { id: string };

const STATUS_LABEL: Record<EvaluationDoc["status"], string> = {
  queued: "待機中",
  running: "評価中",
  succeeded: "完了",
  failed: "失敗",
};

function mb(n: number): string {
  return `${(n / 1024 / 1024).toFixed(0)}MB`;
}

/**
 * ⑦作業動画のアップロード / ⑨評価結果の閲覧。
 *
 * 評価は analyze(実API)を伴うため、回数上限は Cloud Functions 側で強制する
 * (設計 §4.2)。画面はエラーを出すだけで、上限そのものは持たない。
 */
export function Evaluations() {
  const { user, profile, paths } = useAuth();
  const { t, locale, lang } = useI18n();
  const [dishes, setDishes] = useState<(DishDoc & { id: string })[]>([]);
  const [rows, setRows] = useState<Row[] | null>(null);
  // 評価文書の dishName は実行時の日本語名の写し。中国語表示では今の品目の訳を引く
  // (削除済み・担当外の品目は一覧に無いので写しのまま出す)
  const nameOf = (r: Row) =>
    (lang === "zh" ? dishTitle(dishes.find((d) => d.id === r.dishId), lang) : null) ??
    r.dishName ??
    r.dishId;
  const [dishId, setDishId] = useState("");
  const [videos, setVideos] = useState<File[]>([]);
  const [progress, setProgress] = useState<{ done: number; total: number } | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  // 押す前から残回数が見えるようにする(quotas は本人だけ読める)。
  // null = 無制限(テナント設定で上限を外している)
  const [remaining, setRemaining] = useState<number | null>(null);
  const [used, setUsed] = useState<number | null>(null);
  const [dailyLimit, setDailyLimit] = useState<number | null>(DEFAULT_DAILY_LIMIT);

  const isStaff = profile?.role === "admin" || profile?.role === "chef";
  const isAdmin = profile?.role === "admin";
  // 削除した評価は既定で隠す。管理者だけが表示して元に戻せる
  const [showDeleted, setShowDeleted] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const deletedCount = rows?.filter((r) => r.deletedAt).length ?? 0;
  const visibleRows = rows?.filter((r) => !r.deletedAt || (isAdmin && showDeleted)) ?? null;

  useEffect(() => {
    if (!user) return;
    if (!paths) return;
    const base = paths.dishes();
    const q = isStaff
      ? query(base, orderBy("updatedAt", "desc"))
      : query(base, where("assigneeUids", "array-contains", user.uid), orderBy("updatedAt", "desc"));
    return onSnapshot(q, (snap) =>
      setDishes(
        snap.docs
          .map((d) => ({ id: d.id, ...(d.data() as DishDoc) }))
          // 削除済みの品目は評価の選択肢に出さない(過去の評価は dishName で表示される)
          .filter((d) => d.currentAnalysisPath && !d.deletedAt),
      ),
    );
  }, [user, isStaff, paths]);

  useEffect(() => {
    if (!user) return;
    // 決定2: 本人 + 上長。スタッフは全件。
    if (!paths) return;
    const base = paths.evaluations();
    const q = isStaff
      ? query(base, orderBy("createdAt", "desc"))
      : query(
          base,
          or(where("traineeUid", "==", user.uid), where("supervisorUid", "==", user.uid)),
          orderBy("createdAt", "desc"),
        );
    return onSnapshot(
      q,
      (snap) => setRows(snap.docs.map((d) => ({ id: d.id, ...(d.data() as EvaluationDoc) }))),
      (e) => setError(e.message),
    );
  }, [user, isStaff, paths]);

  useEffect(() => {
    if (!user) return;
    if (!paths) return;
    return onSnapshot(paths.quotaDoc(user.uid), (snap) => {
      // 日本時間の日付で区切る(Functions 側と揃える)
      const today = new Date(Date.now() + 9 * 3600 * 1000).toISOString().slice(0, 10);
      setUsed(snap.exists() && snap.get("date") === today ? Number(snap.get("count") ?? 0) : 0);
    });
  }, [user, paths]);

  // 上限はテナント設定が正(#129)。未設定なら既定、0以下なら無制限
  useEffect(() => {
    if (!paths) return;
    return onSnapshot(paths.tenantDoc(), (snap) => {
      const raw = snap.get("dailyEvalLimit");
      const n = raw == null ? DEFAULT_DAILY_LIMIT : Number(raw);
      setDailyLimit(!Number.isFinite(n) || n <= 0 ? null : n);
    });
  }, [paths]);

  useEffect(() => {
    if (used === null) return;
    setRemaining(dailyLimit === null ? null : Math.max(0, dailyLimit - used));
  }, [used, dailyLimit]);

  const selectedDish = useMemo(() => dishes.find((d) => d.id === dishId), [dishes, dishId]);
  const videoWarnings = useMemo(() => inspectVideoFiles(videos), [videos]);

  function move(i: number, delta: number) {
    const j = i + delta;
    if (j < 0 || j >= videos.length) return;
    const next = [...videos];
    [next[i], next[j]] = [next[j], next[i]];
    setVideos(next);
  }

  async function onSubmit() {
    if (!user || videos.length === 0 || !dishId) return;
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      // 分割録画は結合して1本として評価する(#131)。並び順のまま連番で上げる
      const stamp = Date.now();
      const total = videos.reduce((n, v) => n + v.size, 0);
      const videoPaths: string[] = [];
      let done = 0;
      setProgress({ done: 0, total });
      for (const [i, video] of videos.entries()) {
        const safe = video.name.replace(/[^\w.\-　-鿿]/g, "_");
        const path = `uploads/evaluations/${user.uid}/${stamp}-${String(i + 1).padStart(3, "0")}-${safe}`;
        // 拡張子を失ったファイル(「…MP4 のコピー」等)でも受けられるよう、
        // ブラウザが判定できないときは名前から補う
        const contentType = videoContentType(video);
        if (!contentType) throw new Error(VIDEO_TYPE_ERROR);
        const task = uploadBytesResumable(ref(storage, path), video, { contentType });
        const base = done;
        await new Promise<void>((resolve, reject) => {
          task.on(
            "state_changed",
            (s) => setProgress({ done: base + s.bytesTransferred, total }),
            reject,
            () => resolve(),
          );
        });
        done += video.size;
        videoPaths.push(path);
      }
      const res = await requestEvaluation({ dishId, videoPaths });
      setNotice(
        res.data.remaining == null
          ? t("評価を開始しました。")
          : t("評価を開始しました。本日はあと {n} 回実行できます。", { n: res.data.remaining }),
      );
      setVideos([]);
      setProgress(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      setProgress(null);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="page">
      <header className="page-head">
        <h1>{t("作業の評価")}</h1>
      </header>

      <div className="card">
        <h2>{t("自分の作業動画を評価する")}</h2>
        {dishes.length === 0 ? (
          <p className="muted">
            {t("評価できる品目がありません。マニュアルが作成済みで、担当に割り当てられた品目が必要です。")}
          </p>
        ) : (
          <>
            <label htmlFor="dish">{t("品目")}</label>
            <select id="dish" value={dishId} onChange={(e) => setDishId(e.target.value)}>
              <option value="">{t("選択してください")}</option>
              {dishes.map((d) => (
                <option key={d.id} value={d.id}>
                  {dishTitle(d, lang)}
                  {!d.isGolden ? t("（マニュアル未承認）") : ""}
                </option>
              ))}
            </select>
            {selectedDish && !selectedDish.isGolden && (
              <p className="muted small">
                ⚠️ {t("この品目のマニュアルはまだ承認されていません。評価結果はレビュー前の内容を基準にしたものになります。")}
              </p>
            )}

            <label htmlFor="video">{t("作業動画")}</label>
            <input
              id="video"
              type="file"
              accept={VIDEO_ACCEPT}
              multiple
              onChange={(e) => setVideos(sortVideoFiles([...(e.target.files ?? [])]))}
            />
            <p className="muted small">
              {t("カメラが自動で分割したファイルはまとめて選んでください。1本の録画として結合して評価します。")}
            </p>
            {videos.length > 0 && (
              <ol className="parts">
                {videos.map((v, i) => (
                  <li key={v.name + i}>
                    <span className="no">{videos.length > 1 ? t("{n}本目", { n: i + 1 }) : t("1本のみ")}</span>
                    <span className="nm">{v.name}</span>
                    <span className="sz">{mb(v.size)}</span>
                    {videos.length > 1 && (
                      <span className="mvs">
                        <button type="button" disabled={i === 0 || busy} onClick={() => move(i, -1)}>
                          ↑
                        </button>
                        <button
                          type="button"
                          disabled={i === videos.length - 1 || busy}
                          onClick={() => move(i, 1)}
                        >
                          ↓
                        </button>
                      </span>
                    )}
                  </li>
                ))}
              </ol>
            )}
            {videoWarnings.map((w, i) => (
              <p key={i} className="muted small">⚠️ {t(w)}</p>
            ))}

            <button
              onClick={onSubmit}
              disabled={busy || videos.length === 0 || !dishId || (dailyLimit !== null && remaining === 0)}
            >
              {busy
                ? t("処理中…")
                : dailyLimit !== null && remaining === 0
                  ? t("本日の上限に達しました")
                  : t("アップロードして評価する")}
            </button>
            <p className="muted small">
              {t("マニュアルと比べて差分を評価します（数分〜十数分かかります）。1日3回まで実行できます")}
              {dailyLimit === null ? (
                <>{t("（本日の回数制限なし）")}</>
              ) : (
                remaining !== null && <>{t("（本日の残り {n} 回）", { n: remaining })}</>
              )}。
            </p>

            {progress && (
              <div className="progress-row">
                <div className="progress-label">
                  <span>{t("アップロード中")}</span>
                  <span className="muted">
                    {mb(progress.done)} / {mb(progress.total)}
                  </span>
                </div>
                <progress value={progress.done} max={progress.total || 1} />
              </div>
            )}
            {notice && <p className="ok">{notice}</p>}
            {error && <p className="error">{error}</p>}
          </>
        )}
      </div>

      <div className="card">
        <h2>{t("評価結果")}</h2>
        {isAdmin && deletedCount > 0 && (
          <label className="small muted">
            <input type="checkbox" checked={showDeleted} onChange={(e) => setShowDeleted(e.target.checked)} />{" "}
            {t("削除した評価も表示する（{n}件）", { n: deletedCount })}
          </label>
        )}
        {visibleRows === null && <p className="muted">{t("読み込み中…")}</p>}
        {visibleRows?.length === 0 && <p className="muted">{t("まだ評価がありません。")}</p>}
        {visibleRows && visibleRows.length > 0 && (
          <table className="table">
            <thead>
              <tr>
                <th>{t("品目")}</th>
                <th>{t("状態")}</th>
                <th>{t("スコア")}</th>
                <th>{t("実行")}</th>
                {isAdmin && <th aria-label={t("操作")} />}
              </tr>
            </thead>
            <tbody>
              {visibleRows.map((r) => (
                <Fragment key={r.id}>
                <tr className={r.deletedAt ? "muted" : undefined}>
                  <td data-label={t("品目")}>
                    {r.status === "succeeded" ? (
                      <Link to={`/evaluations/${r.id}`}>{nameOf(r)}</Link>
                    ) : (
                      nameOf(r)
                    )}
                    {!r.goldenWasApproved && (
                      <div className="muted small">{t("基準マニュアルは未承認")}</div>
                    )}
                    {r.deletedAt && (
                      <div className="muted small">
                        {t("削除済み（{when}・{name}）", {
                          when: r.deletedAt.toDate().toLocaleString(locale),
                          name: r.deletedByName ?? "-",
                        })}
                        {r.deleteReason && `　${t("削除の理由:")} ${r.deleteReason}`}
                      </div>
                    )}
                  </td>
                  <td data-label={t("状態")}>
                    <span className={`badge ${r.status}`}>{t(STATUS_LABEL[r.status])}</span>
                    {r.error && <div className="error small">{r.error}</div>}
                  </td>
                  <td data-label={t("スコア")}>
                    {r.score != null ? (
                      <span className={`score band-${scoreBand(r.score)}`}>
                        {r.score}
                        <span className="muted small">/100</span>
                      </span>
                    ) : (
                      "—"
                    )}
                    {/* 通信簿(8/19 MTG): 味は別の数字として小さく併記。未評価は工程のみ */}
                    {r.tasting?.score != null && (
                      <span className="muted small">　／ {t("味")} {r.tasting.score}</span>
                    )}
                  </td>
                  <td className="muted small" data-label={t("実行")}>
                    {r.createdAt ? r.createdAt.toDate().toLocaleString(locale) : "—"}
                  </td>
                  {isAdmin && (
                    <td className="small" data-label={t("操作")}>
                      {r.deletedAt ? (
                        <RestoreEvaluation evaluationId={r.id} />
                      ) : (
                        <button
                          className="link danger-link"
                          disabled={deletingId === r.id}
                          onClick={() => setDeletingId(r.id)}
                        >
                          {t("削除")}
                        </button>
                      )}
                    </td>
                  )}
                </tr>
                {isAdmin && deletingId === r.id && (
                  <tr>
                    <td colSpan={5}>
                      <DeleteEvaluation
                        evaluationId={r.id}
                        label={`${nameOf(r)}・${r.createdAt ? r.createdAt.toDate().toLocaleString(locale) : "—"}`}
                        onClose={() => setDeletingId(null)}
                      />
                    </td>
                  </tr>
                )}
                </Fragment>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

/**
 * ⑨評価レポートの表示。認可プロキシ(/e/{evalId}/**)越しに取得する。
 *
 * 見た目は商談デモ(ichininmae-web public/demo)に合わせる。数字を大きく出し、
 * 直すところを先に、できたところを後に置く。Markdown の生表示だと
 * 「どこを直せばよいか」を読み手が探すことになるため、構造化 JSON から組む。
 */
export function EvaluationReport() {
  const { evalId } = useParams<{ evalId: string }>();
  const [view, setView] = useState<EvaluationView | null>(null);
  const [error, setError] = useState<string | null>(null);
  const { user, profile, paths } = useAuth();
  const { t, locale } = useI18n();
  // null = 確認中 / false = この評価には動画が残っていない(対応前に実行された評価)
  const [hasVideo, setHasVideo] = useState<boolean | null>(null);
  // 論理削除された評価も、リンクを知っていれば開ける(元に戻す判断のため)。その旨を出す
  const [deleted, setDeleted] = useState<{ when: Date | null; name: string | null; reason: string | null } | null>(
    null,
  );
  // 食味評価(2026-08-19 熊谷さんMTG: 通信簿形式)。tastingAxes 未設定のテナントでは
  // 味のUIを一切出さない(機能フラグ)
  const [tastingConfig, setTastingConfig] = useState<TastingAxes | null>(null);
  const [tastingEntries, setTastingEntries] = useState<(TastingEntry & { id: string })[]>([]);

  const videoUrl = `/e/${evalId}/trainee.mp4`;

  const load = useCallback(async () => {
    setError(null);
    setView(null);
    try {
      const r = await fetchFromProxy(`/e/${evalId}/evaluation_result.json`);
      if (!r.ok) {
        throw new Error(
          r.status === 401
            ? t("ログインの有効期限が切れている可能性があります。ログインし直してください (401)")
            : t("評価結果を取得できませんでした ({status})", { status: r.status }),
        );
      }
      setView(toView((await r.json()) as EvaluationResult));
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }, [evalId]);

  useEffect(() => {
    void load();
  }, [load]);

  // 動画を出せるかは **評価文書の evaluatedVideoPath** で決める。
  // この対応より前に実行された評価には動画が無いので、出し分けが要る。
  //
  // プロキシへ HTTP で確かめに行かないのは、大きな動画が署名付きURL(GCS)への
  // リダイレクトになるため。fetch はクロスオリジンのリダイレクトを CORS で弾く
  // (<video> や <a> は影響を受けないので、再生とリンクはそのまま動く)。
  useEffect(() => {
    if (!paths || !evalId) return;
    return onSnapshot(
      paths.evaluationDoc(evalId),
      (snap) => {
        setHasVideo(Boolean(snap.get("evaluatedVideoPath")));
        const at = snap.get("deletedAt") as { toDate(): Date } | null | undefined;
        setDeleted(
          at
            ? {
                when: at.toDate(),
                name: (snap.get("deletedByName") as string | null) ?? null,
                reason: (snap.get("deleteReason") as string | null) ?? null,
              }
            : null,
        );
      },
      () => setHasVideo(false),
    );
  }, [paths, evalId]);

  // 軸はテナント設定(欣葉側の意思決定事項)。コードに焼かない
  useEffect(() => {
    if (!paths) return;
    return onSnapshot(paths.tenantDoc(), (snap) =>
      setTastingConfig((snap.get("tastingAxes") as TastingAxes | undefined) ?? null),
    );
  }, [paths]);

  useEffect(() => {
    if (!paths || !evalId) return;
    if (!tastingEnabled(tastingConfig)) return;
    return onSnapshot(paths.tasting(evalId), (snap) =>
      setTastingEntries(snap.docs.map((d) => ({ id: d.id, ...(d.data() as TastingEntry) }))),
    );
  }, [paths, evalId, tastingConfig]);

  // 代表 = 最新 updatedAt(Functions の非正規化と同じ規則)。先頭に出す
  const tastingViews = useMemo(() => {
    if (!tastingEnabled(tastingConfig)) return [];
    return sortTastingEntries(tastingEntries).map((e) => ({
      id: e.id,
      ...toTastingView(e, tastingConfig),
    }));
  }, [tastingConfig, tastingEntries]);
  const tastingRep = tastingViews[0] ?? null;
  const ownTasting = user ? (tastingEntries.find((e) => e.evaluatorUid === user.uid) ?? null) : null;
  const canTaste =
    profile?.role === "admin" || profile?.role === "chef" || profile?.role === "supervisor";

  if (error) {
    return (
      <div className="page">
        <p><Link to="/evaluations">{t("← 評価一覧")}</Link></p>
        <p className="error">{error}</p>
        <button onClick={() => void load()}>{t("もう一度読み込む")}</button>
      </div>
    );
  }
  if (!view) return <div className="page"><p className="muted">{t("読み込み中…")}</p></div>;
  /** 「工程3: 油通し」。工程名の無い古い結果は「工程3」だけ(#122) */
  const stepLabel = (n: number) => {
    const name = view.stepNames[n];
    return name ? t("工程{n}: {name}", { n, name }) : t("工程{n}", { n });
  };

  return (
    <div className="page">
      <p><Link to="/evaluations">{t("← 評価一覧")}</Link></p>
      <header className="page-head">
        <h1>{t("評価結果")}</h1>
        <span className="muted small">{view.dishName}</span>
      </header>
      {deleted && (
        <div className="warn coverage-warn">
          <strong>
            {t("この評価は削除されています（{when}・{name}）。評価一覧と熟達度の表には表示されません。", {
              when: deleted.when ? deleted.when.toLocaleString(locale) : "",
              name: deleted.name ?? "-",
            })}
          </strong>
          {deleted.reason && (
            <p className="small">
              {t("削除の理由:")} {deleted.reason}
            </p>
          )}
          {profile?.role === "admin" && evalId && (
            <div className="prep-import-row">
              <RestoreEvaluation evaluationId={evalId} />
            </div>
          )}
        </div>
      )}

      <div className="kpis">
        {/* 通信簿(8/19 MTG)。工程と味は別の数字で、合算しない。
            味が未入力のときは「試食待ち」(0点と誤読させない) */}
        {tastingEnabled(tastingConfig) && (
          <div className="kpi tasting-head">
            <div className="kpi-label">{t("通信簿")}</div>
            <div className="kpi-num">
              {t("工程")} {view.score}
              <small>{t("点")}</small>
              <span className="tasting-sep">／</span>
              {t("味")}{" "}
              {tastingRep?.total != null ? (
                <>
                  {tastingRep.total}
                  <small>{t("点")}</small>
                </>
              ) : (
                <span className="tasting-wait">{t("試食待ち")}</span>
              )}
            </div>
            <div className="kpi-foot">{t("味は担当のコーチが試食して評価します")}</div>
          </div>
        )}
        <div className={`kpi band-${view.band}`}>
          <div className="kpi-label">{t("総合スコア")}</div>
          <div className="kpi-num">
            {view.score}
            <small>/ 100</small>
          </div>
          <div className="kpi-foot">{t(BAND_LABEL[view.band])}</div>
        </div>
        <div className={`kpi ${view.problems.length > 0 ? "alert" : ""}`}>
          <div className="kpi-label">{t("直したいところ")}</div>
          <div className="kpi-num">
            {view.problems.length}
            <small>{t("件")}</small>
          </div>
        </div>
        <div className="kpi">
          <div className="kpi-label">{t("できていたところ")}</div>
          <div className="kpi-num">
            {view.covered.length}
            <small>{t("件")}</small>
          </div>
        </div>
        <div className="kpi">
          <div className="kpi-label">{t("確認できなかった")}</div>
          <div className="kpi-num">
            {view.unknowns.length}
            <small>{t("件")}</small>
          </div>
          <div className="kpi-foot">{t("減点していません")}</div>
        </div>
      </div>

      {/* 評価した動画そのもの。文章だけだと「何を見て採点したのか」が分からない。
          原本ではなく、エンジンが実際に見た 720p の1本を出す(分割録画は結合済み) */}
      {hasVideo && (
        <div className="card">
          <h2>{t("評価した動画")}</h2>
          <video
            className="evaluated-video"
            src={videoUrl}
            controls
            preload="metadata"
            playsInline
          />
          <p className="muted small">
            {t("この評価に使った動画です（画質は 720p に落としてあります。分割して撮影されたファイルは1本に結合しています）。")}
            <a href={videoUrl} target="_blank" rel="noreferrer">
              {t("別のタブで開く")}
            </a>
          </p>
        </div>
      )}
      {hasVideo === false && (
        <div className="card">
          <h2>{t("評価した動画")}</h2>
          <p className="muted small">
            {t("この評価には動画が残っていません（動画を保存するようにする前に実行された評価です）。もう一度評価を実行すると、次からはここで見られます。")}
          </p>
        </div>
      )}

      {view.coverage.length > 0 && (
        <div className="card">
          <h2>{t("項目ごとの充足")}</h2>
          {view.coverage.map((c) => (
            <div key={c.category} className="cov-row">
              <span className="cov-label">{t(c.label)}</span>
              <span className="cov-bar" aria-hidden="true">
                <span className={`cov-fill band-${scoreBand(c.ratio * 100)}`} style={{ width: `${Math.round(c.ratio * 100)}%` }} />
              </span>
              <span className="cov-num">
                {Math.round(c.ratio * 100)}%
                <span className="muted small">（{c.ok}/{c.total}）</span>
              </span>
            </div>
          ))}
          <p className="muted small">
            {t("マニュアルの項目が作業動画で確認できるかを複数回みて、多数決で判定しています。工程の分け方や順序、作業の速さは評価に入れていません。")}
          </p>
        </div>
      )}

      <div className="card">
        <h2>{t("直したいところ（{n}件）", { n: view.problems.length })}</h2>
        {view.problems.length === 0 ? (
          <p className="ok">{t("マニュアルの項目はすべて確認できました。")}</p>
        ) : (
          view.problems.map((i, n) => (
            <div key={n} className="focus-item">
              <span className={`tag ${i.verdict}`}>{t(VERDICT_LABEL[i.verdict])}</span>
              <div className="focus-body">
                <b>{i.item}</b>
                <span className="muted small">　{t(CATEGORY_LABEL[i.category])}</span>
                {/* どの工程の指摘か(#122)。マニュアルの同じ工程を開けば見直せる */}
                {i.step_no != null && <span className="muted small">　{stepLabel(i.step_no)}</span>}
                {i.evidence && (
                  <span className="why">
                    {t("動画では:")} {i.evidence}
                    {i.at_sec != null && `（${mmss(i.at_sec)}）`}
                  </span>
                )}
                {i.feedback && <span className="why">{i.feedback}</span>}
                {/* 該当箇所を現物で見せる(#126)。言葉だけだと動画を頭から探すことになる */}
                {i.clip ? (
                  <video
                    className="evidence-media"
                    src={`/e/${evalId}/${i.clip}`}
                    poster={i.frame ? `/e/${evalId}/${i.frame}` : undefined}
                    controls
                    preload="none"
                    playsInline
                  />
                ) : (
                  i.frame && (
                    <img className="evidence-media" src={`/e/${evalId}/${i.frame}`} alt="該当箇所" />
                  )
                )}
              </div>
            </div>
          ))
        )}
      </div>

      {view.unknowns.length > 0 && (
        <div className="card note-shu">
          <h2>{t("確認できなかったところ（{n}件）", { n: view.unknowns.length })}</h2>
          <p className="muted small">
            {t("動画に映っていないか、判別できなかった項目です。評価は下げていません。手元が映るように撮り直すと、次はここも確認できます。")}
          </p>
          <ul className="plain-list">
            {view.unknowns.map((u, n) => (
              <li key={n}>
                <span className="muted small">
                  {t(CATEGORY_LABEL[u.category])}
                  {u.step_no != null && `・${stepLabel(u.step_no)}`}
                </span>
                　{u.text}
                {/* 基準と違うと判定されたのに何が違うかを示せなかったもの(#128) */}
                {u.reason === "no_reason_given" && (
                  <span className="why">
                    {t("基準と違うと判定されましたが、何が違うかが示されなかったため保留しました。")}
                  </span>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}

      {view.extras.length > 0 && (
        <div className="card">
          <h2>{t("マニュアルに無かった動き（{n}件）", { n: view.extras.length })}</h2>
          <p className="muted small">
            {t("余分とは限りません。お手本に足すべき手順であれば、マニュアル側を直してください。")}
          </p>
          <ul className="plain-list">
            {view.extras.map((e, n) => (
              <li key={n}>
                <b>{e.item}</b>
                {e.feedback && <span className="why">{e.feedback}</span>}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* マニュアルと同じ工程の並びで、その工程の項目をすべて出す(#122)。
          工程番号を持たない古い結果は、従来どおり「できていたところ」だけを出す */}
      {view.bySteps.length > 0 && (
        <div className="card">
          <h2>{t("工程ごとの結果")}</h2>
          <p className="muted small">
            {t("マニュアルと同じ工程の順に並べています。✓ できている　! 基準と違う　× 見当たらない　? 確認できず")}
          </p>
          {view.bySteps.map((g) => (
            <div key={g.stepNo ?? "none"} className="cov-group">
              <h3>{g.stepNo == null ? t("盛り付け・工程に紐づかない項目") : stepLabel(g.stepNo)}</h3>
              <ul className="check-list">
                {g.entries.map((e, n) => (
                  <li key={n} className={`v-${e.verdict}`}>
                    {e.text}
                    <span className="verdict-note">
                      {t(CATEGORY_LABEL[e.category])}
                      {e.verdict !== "covered" &&
                        `・${e.verdict === "unknown" ? t("確認できず") : t(VERDICT_LABEL[e.verdict])}`}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      )}

      {view.bySteps.length === 0 && view.covered.length > 0 && (
        <div className="card">
          <h2>{t("できていたところ（{n}件）", { n: view.covered.length })}</h2>
          {groupByCategory(view.covered).map((g) => (
            <div key={g.category} className="cov-group">
              <h3>{t(g.label)}</h3>
              <ul className="check-list">
                {g.items.map((i, n) => (
                  <li key={n}>{i.item}</li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      )}

      {view.overallFeedback && (
        <div className="card">
          <h2>{t("全体の講評")}</h2>
          <p>{view.overallFeedback}</p>
        </div>
      )}

      {/* 食味評価(8/19 MTG)。味はカメラに映らないため人(コーチ)が入れる。
          代表(最新)を先頭に全件を並べる。軸未設定のテナントでは出さない */}
      {tastingEnabled(tastingConfig) && (
        <div className="card">
          <h2>{t("食味評価")}</h2>
          {tastingViews.length === 0 ? (
            <p className="muted">
              {t("まだ食味の評価がありません。試食のうえ評価が入ると、ここに表示されます。")}
            </p>
          ) : (
            tastingViews.map((v) => (
              <div key={v.id} className="tasting-entry">
                <div className="tasting-entry-head">
                  <b className="tasting-total">{v.total != null ? t("{n}点", { n: v.total }) : "—"}</b>
                  <span className="muted small">
                    {v.evaluatorName}（{t(ROLE_LABEL[v.evaluatorRole] ?? v.evaluatorRole)}）
                    {v.at && `　${v.at.toLocaleString(locale)}`}
                  </span>
                </div>
                <ul className="plain-list">
                  {v.axes.map((a) => (
                    <li key={a.axisId}>
                      {a.label}　<b>{a.score}</b>
                      <span className="muted small"> / 5</span>
                      {a.comment && <span className="why">{a.comment}</span>}
                    </li>
                  ))}
                </ul>
                {v.comment && <p className="tasting-comment">{v.comment}</p>}
              </div>
            ))
          )}
          {canTaste && user && profile && evalId && tastingEnabled(tastingConfig) && (
            <TastingForm
              // 自分の入力が読み込まれたら編集モードで開き直す
              key={`${tastingConfig.version}:${ownTasting ? "edit" : "new"}`}
              evalId={evalId}
              config={tastingConfig}
              existing={ownTasting}
            />
          )}
        </div>
      )}

      <p className="muted small">
        <a href={`/e/${evalId}/evaluation_report.md`} target="_blank" rel="noreferrer">
          {t("元のレポート（テキスト版）を開く")}
        </a>
      </p>
    </div>
  );
}

/**
 * 食味の入力フォーム(admin / chef / supervisor)。
 *
 * 書けるのは自分名義の1文書だけ(firestore.rules で evaluatorUid == auth.uid を強制)。
 * 集計はここでしない(#21 #40)。親 evaluations への代表値の反映は Functions トリガが行う。
 * 表示名・役割は当時の値を焼き込む(JobDoc の requestedByName と同じ理由)。
 */
function TastingForm({
  evalId,
  config,
  existing,
}: {
  evalId: string;
  config: TastingAxes;
  existing: (TastingEntry & { id: string }) | null;
}) {
  const { user, profile, paths } = useAuth();
  const { t } = useI18n();
  const [scores, setScores] = useState<Record<string, number | null>>(() => {
    const init: Record<string, number | null> = {};
    for (const ax of config.axes) {
      const prev = existing?.axes.find((a) => a.axisId === ax.axisId)?.score;
      init[ax.axisId] = isValidAxisScore(prev) ? prev : null;
    }
    return init;
  });
  const [axisComments, setAxisComments] = useState<Record<string, string>>(() => {
    const init: Record<string, string> = {};
    for (const ax of config.axes) {
      init[ax.axisId] = existing?.axes.find((a) => a.axisId === ax.axisId)?.comment ?? "";
    }
    return init;
  });
  const [comment, setComment] = useState(existing?.comment ?? "");
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  // 役割は保存時にも見る(フォームの出し分けは呼び出し側)
  const role = profile?.role;
  if (!user || !paths || (role !== "admin" && role !== "chef" && role !== "supervisor")) {
    return null;
  }

  const complete = config.axes.every((ax) => isValidAxisScore(scores[ax.axisId]));

  async function onSave() {
    if (!user || !paths || !complete) return;
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      await setDoc(paths.tastingDoc(evalId, user.uid), {
        axes: config.axes.map((ax) => ({
          axisId: ax.axisId,
          score: scores[ax.axisId],
          comment: axisComments[ax.axisId]?.trim() || null,
        })),
        comment: comment.trim() || null,
        // 入力時点の版を焼き込む(軸改訂後も過去の入力を読めるように)
        axesVersion: config.version,
        evaluatorUid: user.uid,
        evaluatorName: profile?.displayName ?? user.email ?? user.uid,
        evaluatorRole: role,
        createdAt: existing?.createdAt ?? serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
      setNotice(t("食味の評価を保存しました。"));
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="tasting-form">
      <h3>{existing ? t("自分の評価を修正する") : t("食味を評価する")}</h3>
      {config.axes.map((ax) => (
        <div key={ax.axisId} className="tasting-form-axis">
          <label>{ax.label}</label>
          {ax.description && <p className="muted small">{ax.description}</p>}
          <div className="tasting-scale" role="radiogroup" aria-label={ax.label}>
            {[1, 2, 3, 4, 5].map((n) => (
              <label key={n} className="tasting-scale-item">
                <input
                  type="radio"
                  name={`tasting-${ax.axisId}`}
                  checked={scores[ax.axisId] === n}
                  onChange={() => setScores((s) => ({ ...s, [ax.axisId]: n }))}
                />
                {n}
              </label>
            ))}
          </div>
          <input
            type="text"
            placeholder={t("この軸へのコメント（任意）")}
            value={axisComments[ax.axisId] ?? ""}
            onChange={(e) =>
              setAxisComments((c) => ({ ...c, [ax.axisId]: e.target.value }))
            }
          />
        </div>
      ))}
      <label htmlFor="tasting-comment">{t("全体への一言（任意）")}</label>
      <textarea
        id="tasting-comment"
        rows={2}
        value={comment}
        onChange={(e) => setComment(e.target.value)}
      />
      <button onClick={() => void onSave()} disabled={busy || !complete}>
        {busy ? t("保存中…") : existing ? t("評価を更新する") : t("評価を保存する")}
      </button>
      {!complete && (
        <p className="muted small">{t("すべての軸に1〜5の評価を入れると保存できます。")}</p>
      )}
      {notice && <p className="ok">{notice}</p>}
      {error && <p className="error">{error}</p>}
    </div>
  );
}
